import { DateFormat } from '@equip.health/ui';
import { DateTime, Interval } from 'luxon';

import { Path } from '~/routes/constants';

import { getPreferredFullName } from './patient.util';
import { getTimezoneCodeToValueMapper } from './timezone.util';
import { CalendarListTypeOption } from '../constants/appointment.constants';
import { CareTeamProviderType } from '../constants/patients.constants';
import {
  AllowedParticipants,
  CONFLICTS_RECURRENCE_CHECK_DAYS,
  defaultAllowedParticipantsMapper,
  participantsOptionMapper,
} from '../constants/scheduling.constants';
import { toISO, toISORemoveMillis } from '../utils';
import type {
  ScheduledAppointments,
  ScheduledApppointmentsResponse,
} from '~/components/common/calendar/EditAppointment/util';

const ZOOM_BEFORE_AVAILABLE_MINUTES = 15;

export const DEFAULT_AVAILABILITY_SLOT_INTERVAL = 30;

export const DEFAULT_AVAILABILITY_SETTINGS_TIMEZONE_CODE = 'PST';

export { SYSTEM_TIME_ZONE } from './timezone.util';

export enum CalendarTab {
  SCHEDULE,
  GROUP_CLASS,
  GROUP,
  AVAILABILITY,
  INTEGRATION,
}

export enum ScheduleViewTab {
  DAY,
  WEEK,
}

export const AVAILABILITY_BASE_URL = 'https://api.cronofy.com/v1/availability';

export const CalendarTabsCollection: Record<CalendarTab, TabDefinition> = {
  [CalendarTab.SCHEDULE]: {
    label: 'Schedule',
    subPath: 'schedule',
  },
  [CalendarTab.GROUP_CLASS]: {
    label: 'Classes',
    subPath: 'groups',
  },
  [CalendarTab.GROUP]: {
    label: 'Groups',
    subPath: 'groups_v2', //TODO: this path groups_v2 will be updated along with groups-restructuring FF
  },
  [CalendarTab.AVAILABILITY]: {
    label: 'Availability',
    subPath: 'availability',
  },
  [CalendarTab.INTEGRATION]: {
    label: 'Integration',
    subPath: 'integration',
  },
};

export const CalendarTabsCollectionV2: Record<CalendarTab, TabDefinition> = {
  [CalendarTab.SCHEDULE]: {
    label: 'Schedule',
    subPath: 'schedule',
  },
  [CalendarTab.GROUP_CLASS]: {
    label: 'Groups',
    subPath: 'groups',
  },
  [CalendarTab.GROUP]: {
    label: 'Groups',
    subPath: 'groups_v2', //TODO: this path groups_v2 will be updated along with groups-restructuring FF
  },
  [CalendarTab.AVAILABILITY]: {
    label: 'Availability',
    subPath: 'availability',
  },
  [CalendarTab.INTEGRATION]: {
    label: 'Integration',
    subPath: 'integration',
  },
};

export const getTabIndex = (path: string): number => {
  const idx = Object.values(CalendarTabsCollection).findIndex(
    ({ subPath }: TabDefinition) => subPath === path,
  );
  return Math.max(idx, CalendarTab.SCHEDULE);
};

export const getAppointmentTypeDescription = (
  duration: number,
  buffer: number,
): string => `${duration} min meeting with ${buffer} min break`;

export const supportedIanaTimezones = [
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/London',
  'Asia/Calcutta',
];

export const getZoneNameDisplay = (ianaZone: string): string => {
  if (!supportedIanaTimezones.includes(ianaZone)) return '';
  const now = DateTime.now().setZone(ianaZone);
  return `(UTC${now.toFormat('Z')}) ${
    ianaZone === 'America/Phoenix' ? 'Arizona' : now.offsetNameLong
  }`;
};

const TIMEZONE_DISPLAY_OVERRIDES: Record<string, string> = {
  'America/Phoenix': 'Arizona',
};
export const getTimeZoneOnly = (ianaZone: string): string => {
  if (!supportedIanaTimezones.includes(ianaZone)) {
    return '';
  }

  if (TIMEZONE_DISPLAY_OVERRIDES[ianaZone]) {
    return TIMEZONE_DISPLAY_OVERRIDES[ianaZone];
  }

  const now = DateTime.now().setZone(ianaZone);
  return now.offsetNameLong;
};

/**
 * Given the results of a provider search, determine if the count should be
 * as given OR incremented/decremented as follows
 *   - current user is included in the search result and has the required permission => GIVEN COUNT
 *   - current user is included in the search result and does not have the required permission => DECREMENT BY 1
 *   - current user is not included in the search result and has the required permission => INCREMENT BY 1
 *   - current user is not included in the search result and does not have the required permission => GIVEN COUNT
 */
export const getProviderSearchResultsTotalCount = (
  currentProviderResults: GetProviderByIdResponse[],
  givenCount: number,
  currentUserId: string,
  hasPermission: boolean,
): number =>
  currentProviderResults?.some(
    ({ userId }: GetProviderByIdResponse): boolean => userId === currentUserId,
  )
    ? givenCount + (hasPermission ? 0 : -1)
    : givenCount + (hasPermission ? 1 : 0);

/** Transforms the time label format from "h:mm A" to "h A", e.g. 12:00 AM -> 12 AM */
export const formatTimeLabels = (): void => {
  Array.from(
    document.getElementsByClassName('availability-rules__label--time'),
  ).forEach((elem) => {
    // eslint-disable-next-line no-param-reassign
    elem.textContent = elem.textContent.replace(':00', '');
  });
};

/** Display the appropriate helper text for managing availability given the user has the proxy permission or not  */
export const getManageAvailabilitySelectProviderHelperText = (
  hasProxyPermission: boolean,
): string => {
  const action = 'Please choose';
  const subject = hasProxyPermission
    ? 'a provider and an appointment type'
    : 'an appointment type';

  return `${action} ${subject} to set weekly recurring availability below`;
};

export enum Recurrence {
  DOES_NOT_REPEAT = 'Does not repeat',
  WEEKLY = 'Weekly',
  BIWEEKLY = 'Biweekly',
  MONTHLY = 'Monthly',
}

export enum MonthlyAllowedAppointmentTypes {
  MedicalIntake80Mins = 'Medical Intake, 80 minutes',
  MedicalIntake50Mins = 'Medical Intake, 50 minutes',
  MedicalSession25Mins = 'Medical Session, 25 minutes',
  MedicalSession15Mins = 'Medical Session, 15 minutes',
  MedicalSession50Mins = 'Medical Session, 50 minutes',
  MedicalAssistant = 'Medical Assistant Session, 15 minutes',
  RegisteredNurse25minSession = 'Registered Nurse Session, 25 minutes',
  RegisteredNurse15minSession = 'Registered Nurse Session, 15 minutes',
}

export const recurrenceLabelMapper: Record<Recurrence, string> = {
  [Recurrence.DOES_NOT_REPEAT]: 'Does not repeat',
  [Recurrence.WEEKLY]: 'Weekly',
  [Recurrence.BIWEEKLY]: 'Biweekly',
  [Recurrence.MONTHLY]: 'Every 4 weeks',
};

type GenerateAvailabilityQueryParams = {
  availabilityRule: AppointmentType;
  hostCalendarInfo: UserCalendarInfo;
  ignoreAvailabilitySettings: boolean;
  intervalInMinutes?: number;
  providerCalendarsInfo: UserCalendarInfo[];
  query_periods: AppointmentDetailTime[];
  requiredParticipants?: number | 'all';
  excluded_events?: { event_id?: string; event_uid?: string }[];
  shouldAllProvidersAvailable?: boolean;
};

export const getAvailabilityRuleIdsForProvider = ({
  ignoreAvailabilitySettings,
  shouldAllProvidersAvailable,
  cronofyRule,
}: {
  ignoreAvailabilitySettings: boolean;
  shouldAllProvidersAvailable: boolean;
  cronofyRule: string;
}) => {
  if (ignoreAvailabilitySettings || !shouldAllProvidersAvailable) return {};

  return {
    availability_rule_ids: [cronofyRule],
    managed_availability: true,
  };
};

export const generateAvailabilityQuery = ({
  availabilityRule,
  hostCalendarInfo,
  ignoreAvailabilitySettings,
  intervalInMinutes = DEFAULT_AVAILABILITY_SLOT_INTERVAL,
  providerCalendarsInfo,
  query_periods,
  excluded_events,
  shouldAllProvidersAvailable = false,
}: GenerateAvailabilityQueryParams): CalendarResourceConfigAvailabilityQuery => {
  const { bufferInMinutes, durationInMinutes, cronofyRule, appointmentType } =
    availabilityRule;

  const hostMember: CalendarResourceConfigAvailabilityQueryParticipantMember = {
    calendar_ids: [hostCalendarInfo.calendarId],
    sub: hostCalendarInfo.sub,
  };

  if (!ignoreAvailabilitySettings) {
    hostMember.availability_rule_ids = [cronofyRule ?? appointmentType];
    hostMember.managed_availability = true;
  }

  const providerMembers: CalendarResourceConfigAvailabilityQueryParticipantMember[] =
    providerCalendarsInfo.map(({ calendarId, sub }) => ({
      calendar_ids: [calendarId],
      ...getAvailabilityRuleIdsForProvider({
        cronofyRule: cronofyRule ?? appointmentType,
        ignoreAvailabilitySettings,
        shouldAllProvidersAvailable,
      }),
      sub,
    }));

  return {
    buffer: {
      after: { minutes: bufferInMinutes },
      before: { minutes: 0 },
    },
    participants: [
      {
        members: [hostMember, ...providerMembers],
        required: 'all',
      },
    ],
    query_periods,
    required_duration: {
      minutes: durationInMinutes,
    },
    response_format: 'overlapping_slots',
    start_interval: {
      minutes: intervalInMinutes,
    },
    excluded_events,
  };
};

export const getPrimaryProviderTypeForAppointmentType = (
  providerTypeName: string,
): string => {
  const providerTypeLowerCase = providerTypeName?.toLowerCase() ?? '';
  if (
    providerTypeLowerCase ===
    CareTeamProviderType.PSYCHIATRIC_NURSE_PRACTITIONER.toLowerCase()
  )
    return CareTeamProviderType.PSYCHIATRIST;
  if (
    [
      CareTeamProviderType.NURSE_PRACTITIONER.toLowerCase(),
      CareTeamProviderType.PHYSICIAN_ASSISTANT.toLowerCase(),
    ].includes(providerTypeLowerCase)
  )
    return CareTeamProviderType.PHYSICIAN;
  return providerTypeLowerCase;
};

export const hasMatchingProviderTypeName = (
  appointment: AppointmentType,
  providerTypeName: string,
): boolean =>
  appointment?.providerTypeName.toLowerCase() ===
  getPrimaryProviderTypeForAppointmentType(providerTypeName);

export const getRecurrenceDisplay = (
  start: string,
  recurrence: Recurrence,
  recurringUntilDateTime: string,
  shouldShowNew: boolean = false,
): string => {
  const recurringUntilDateTimeConverted: DateTime = DateTime.fromISO(
    recurringUntilDateTime,
  );

  let recurringUntilDateTimeStr = '';
  if (recurringUntilDateTimeConverted.isValid) {
    recurringUntilDateTimeStr = recurringUntilDateTime
      ? ` until ${recurringUntilDateTimeConverted.toFormat(
          DateFormat.shortMonthDayYear,
        )}`
      : '';
  }

  const dayOfWeek = DateTime.fromISO(start).toFormat('cccc');
  if (recurrence === Recurrence.WEEKLY)
    return `Recurring, ${shouldShowNew ? 'every' : 'weekly on'} ${dayOfWeek}${recurringUntilDateTimeStr}`;
  if (recurrence === Recurrence.BIWEEKLY)
    return `Recurring, every two weeks on ${dayOfWeek}${recurringUntilDateTimeStr}`;
  return '';
};

export const getDayDifference = (
  currentDate: DateTime,
  toDate: DateTime,
): number => {
  const days = Math.ceil(
    toDate.startOf('day').diff(currentDate, 'days').toObject().days,
  );
  return days;
};

export const getProviderAutoCompleteOption = (
  selectedProvider: Pick<
    Provider,
    'firstName' | 'lastName' | 'providerTypeName' | 'userId'
  >,
): AutoCompleteOption => {
  const { firstName, lastName, providerTypeName, userId } =
    selectedProvider ?? {};
  return !providerTypeName
    ? null
    : {
        primaryText: getPreferredFullName(null, firstName, lastName),
        secondaryText: [providerTypeName],
        value: userId ?? '',
      };
};
export const getProviderUniversalSelectOption = (
  selectedProvider: GetProviderByIdResponse,
): ProviderUniversalSelectOption => {
  const { firstName, lastName, userId, providerTypeDetails } =
    selectedProvider ?? {};
  return !providerTypeDetails?.name
    ? null
    : {
        label: `${firstName} ${lastName}`,
        primaryText: `${firstName} ${lastName}`,
        providerTypeName: providerTypeDetails?.name,
        secondaryText: providerTypeDetails?.name,
        value: userId ?? '',
        firstName,
        lastName,
        userId,
        ...selectedProvider,
      };
};

export const getGroupedAppointmentTypes = (
  apptTypes: AppointmentType[],
): AppointmentType[] =>
  (apptTypes ?? []).reduce(
    (all: AppointmentType[], current: AppointmentType): AppointmentType[] =>
      all.some(
        (existing: AppointmentType) =>
          existing.appointmentType === current.appointmentType,
      )
        ? all
        : all.concat([current]),
    [],
  );

export const getGroupedCronofyRules = (
  apptTypes: AppointmentType[],
): AppointmentType[] =>
  (apptTypes ?? []).reduce(
    (all, current) =>
      all.some((existing) => existing.cronofyRule === current.cronofyRule)
        ? all
        : all.concat([current]),
    [],
  );

export const formatFullDateString = (date: DateTime): string => {
  return date.toLocaleString(DateTime.DATE_FULL);
};

export const getAllowedParticipants = (
  apptTypeName: string,
  programName: string,
): DefaultAllowedParticipants => {
  return (
    defaultAllowedParticipantsMapper[programName]?.[apptTypeName] ??
    participantsOptionMapper[AllowedParticipants.NONE]
  );
};

export const getInvitedParticipants = (
  participants: Participant[],
): Participant[] => {
  return (participants ?? []).filter(
    (participant: Participant) => !!participant?.isChecked,
  );
};

export const sortParticipants = (
  participants: Participant[],
): Participant[] => {
  return [...(participants ?? [])].sort((a: Participant, b: Participant) =>
    a?.primaryText.localeCompare(b?.primaryText ?? ''),
  );
};

export const getEligibleHostParticipantFromCareTeam = (
  appointmentType: AppointmentType,
  participants: Participant[],
  careTeamMembers: CareTeamMember[],
): Participant =>
  (participants ?? []).find(
    ({ id, isChecked, secondaryText }: Participant) =>
      isChecked &&
      hasMatchingProviderTypeName(appointmentType, secondaryText) &&
      careTeamMembers.some(
        ({ providerExternalId }: CareTeamMember) => providerExternalId === id,
      ),
  );

/** Transforms the time label format to "h:mma" or to "ha" - for on the hour exactly time, e.g. 12:30pm or 12pm vs 12:00pm */
export const formatISOToHourAndMinute = (date: string): string => {
  const dateTime = DateTime.fromISO(date);
  return dateTime.toFormat(dateTime.minute ? 'h:mma' : 'ha').toLowerCase();
};

export const isParticipant = (
  participants: AppointmentAttendee[],
  userId: string,
): boolean => {
  return participants.some(
    (participant: AppointmentAttendee) => participant.externalId === userId,
  );
};

export const showCancelButton = (
  isHost: boolean,
  isSchedulingAdmin: boolean,
): boolean => {
  return isHost || isSchedulingAdmin;
};

export const showZoomButton = ({
  end,
  hostParticipantId,
  providerParticipants,
  start,
  userId,
}: {
  end: string;
  hostParticipantId: string;
  providerParticipants: AppointmentAttendee[];
  start: string;
  userId: string;
}): boolean => {
  return (
    DateTime.now()
      .plus({ minutes: ZOOM_BEFORE_AVAILABLE_MINUTES })
      .diff(DateTime.fromISO(start)).milliseconds > 0 &&
    DateTime.now().diff(DateTime.fromISO(end)).milliseconds < 0 &&
    (userId === hostParticipantId ||
      isParticipant(providerParticipants, userId))
  );
};

/** Calculate end date time from start date time and duration. Result will be in ISO format. */
export const getAppointmentEndTime = (
  startTime: string,
  durationInMinutes: number,
): string =>
  toISO(
    DateTime.fromISO(toISO(startTime)).plus({ minutes: durationInMinutes }),
  );

/** Gets busy intervals between specified start/end dates by merging overlapping appointments */
const getBusyIntervalsFromAppointments = ({
  patientAppointments,
  supportAppointments,
}: ScheduledAppointments): AppointmentDetailTime[] => {
  const uniqueAppointments: ScheduledApppointmentsResponse[] = [];

  for (const appt of patientAppointments.concat(supportAppointments)) {
    if (
      !uniqueAppointments.some(
        (uniqueAppt: ScheduledApppointmentsResponse) =>
          appt.appointmentExternalId === uniqueAppt.appointmentExternalId,
      )
    ) {
      uniqueAppointments.push(appt);
    }
  }

  uniqueAppointments.sort(
    (a, b) =>
      DateTime.fromISO(a.appointmentStartDateTime).toMillis() -
      DateTime.fromISO(b.appointmentStartDateTime).toMillis(),
  );

  if (!uniqueAppointments.length) return [];

  const { appointmentEndDateTime, appointmentStartDateTime } =
    uniqueAppointments[0];

  const busyIntervals = [
    {
      end: toISORemoveMillis(DateTime.fromISO(appointmentEndDateTime)),
      start: toISORemoveMillis(DateTime.fromISO(appointmentStartDateTime)),
    },
  ];

  for (let i = 1; i < uniqueAppointments.length; i += 1) {
    const nextApptStart = DateTime.fromISO(
      uniqueAppointments[i].appointmentStartDateTime,
    );
    const nextApptEnd = DateTime.fromISO(
      uniqueAppointments[i].appointmentEndDateTime,
    );
    const prevApptEnd = DateTime.fromISO(
      busyIntervals[busyIntervals.length - 1].end,
    );
    const doesOverlap =
      nextApptStart.toJSDate().getTime() <= prevApptEnd.toJSDate().getTime();

    // if the next appointment overlaps with the previous busy interval and extends beyond it, extend the previous busy interval
    if (
      doesOverlap &&
      nextApptEnd.toJSDate().getTime() > prevApptEnd.toJSDate().getTime()
    ) {
      busyIntervals[busyIntervals.length - 1].end =
        toISORemoveMillis(nextApptEnd);
    }
    // if there is a gap between the previous busy interval and the next appointment, insert a new busy interval
    else if (!doesOverlap) {
      busyIntervals.push({
        end: toISORemoveMillis(nextApptEnd),
        start: toISORemoveMillis(nextApptStart),
      });
    }
  }

  return busyIntervals;
};

/** Gets free periods between specified start/end dates by merging overlapping appointments */
const getFreePeriodsFromBusyIntervals = (
  busyIntervals: AppointmentDetailTime[],
  start: DateTime,
  end: DateTime,
): AppointmentDetailTime[] => {
  if (!busyIntervals.length)
    return [
      {
        end: toISORemoveMillis(end),
        start: toISORemoveMillis(start),
      },
    ];

  const freePeriods: AppointmentDetailTime[] = [];

  // Add initial free period only if there's a gap between the "start" and the first busy interval
  if (new Date(busyIntervals[0].start).getTime() > start.toJSDate().getTime()) {
    freePeriods.push({
      end: busyIntervals[0].start,
      start: toISORemoveMillis(start),
    });
  }

  // Add gaps between busy intervals
  if (busyIntervals.length > 1) {
    for (let i = 1; i < busyIntervals.length; i += 1) {
      freePeriods.push({
        end: busyIntervals[i].start,
        start: busyIntervals[i - 1].end,
      });
    }
  }

  // Add last free period if there's a gap between the last busy interval and the "end"
  if (
    end.toJSDate().getTime() >
    new Date(busyIntervals[busyIntervals.length - 1].end).getTime()
  ) {
    freePeriods.push({
      end: toISORemoveMillis(end),
      start: busyIntervals[busyIntervals.length - 1].end,
    });
  }

  return freePeriods;
};

const isBetweenDates =
  (start: DateTime, end: DateTime) =>
  (appt: ScheduledApppointmentsResponse): boolean => {
    return (
      new Date(appt.appointmentStartDateTime).getTime() <
        end.toJSDate().getTime() &&
      new Date(appt.appointmentEndDateTime).getTime() >
        start.toJSDate().getTime()
    );
  };

export const getPatientSupportFreePeriods = ({
  end,
  patientAppointments,
  start,
  supportAppointments,
}: {
  end: DateTime;
  patientAppointments: ScheduledApppointmentsResponse[];
  start: DateTime;
  supportAppointments: ScheduledApppointmentsResponse[];
}): AppointmentDetailTime[] => {
  const busyIntervals = getBusyIntervalsFromAppointments({
    patientAppointments: patientAppointments.filter(isBetweenDates(start, end)),
    supportAppointments: supportAppointments.filter(isBetweenDates(start, end)),
  });

  return getFreePeriodsFromBusyIntervals(busyIntervals, start, end);
};

const hasSupportId = (
  appt: ScheduledApppointmentsResponse,
  id: string,
  eventId?: string,
): boolean => {
  if (
    appt &&
    typeof appt === 'object' &&
    'supportAttendeeExternalIds' in appt &&
    Array.isArray((appt as any).supportAttendeeExternalIds)
  ) {
    return (appt as any).supportAttendeeExternalIds.includes(id);
  }

  if (
    appt &&
    typeof appt === 'object' &&
    'supportAttendees' in appt &&
    Array.isArray(appt.supportAttendees)
  ) {
    return (
      appt.supportAttendees.some((s: any) => s?.externalId === id) &&
      (eventId === undefined || appt.eventId !== eventId)
    );
  }

  return false;
};

export const filterSupportAppointments = (
  supportIds: string[],
  supportAppts: ScheduledApppointmentsResponse[],
  eventId?: string,
): ScheduledApppointmentsResponse[] => {
  const ids = supportIds ?? [];
  return (supportAppts ?? []).filter((appt) =>
    ids.some((id) => hasSupportId(appt, id, eventId)),
  );
};

const eventDateFiltersByScheduleViewMapper: Record<
  ScheduleViewTab,
  (date: DateTime) => GetFetchEventsDateFilters
> = {
  [ScheduleViewTab.DAY]: (date: DateTime) => ({
    endDate: toISO(date.endOf('day')),
    startDate: toISO(date.startOf('day')),
  }),
  [ScheduleViewTab.WEEK]: (date: DateTime) => ({
    endDate: toISO(date.endOf('week').minus({ days: 1 })),
    startDate: toISO(date.startOf('week').minus({ days: 1 })),
  }),
};

const getDayDuration = (date: DateTime) => ({
  endDate: toISO(date.endOf('day')),
  startDate: toISO(date.startOf('day')),
});

// Luxon weeks start Monday (ISO 8601). On Sundays (weekday 7), startOf('week')
// returns the previous Monday, placing Sunday in the prior US-style week.
export const getUSWeekStartMonday = (date: DateTime): DateTime =>
  date.weekday === 7
    ? date.startOf('week').plus({ weeks: 1 })
    : date.startOf('week');

const eventDateFiltersByScheduleViewMapperV2: Record<
  AppointmentListType,
  (date: DateTime) => GetFetchEventsDateFilters
> = {
  [CalendarListTypeOption.DAILY_VIEW]: getDayDuration,
  [CalendarListTypeOption.WEEKLY_VIEW]: (date: DateTime) => {
    const weekStart = getUSWeekStartMonday(date);
    return {
      startDate: toISO(weekStart.minus({ days: 1 })),
      endDate: toISO(weekStart.plus({ days: 5 }).endOf('day')),
    };
  },
  [CalendarListTypeOption.GROUPS]: getDayDuration,
};

export const getFetchEventsDateFilters = (
  tab: ScheduleViewTab,
  date: DateTime,
): GetFetchEventsDateFilters => {
  return eventDateFiltersByScheduleViewMapper[tab](date);
};

export const getFetchEventsDateFiltersV2 = (
  listType: AppointmentListType,
  date: DateTime,
): GetFetchEventsDateFilters => {
  return eventDateFiltersByScheduleViewMapperV2[listType](date);
};

const dateHeaderByScheduleViewMapper: Record<
  ScheduleViewTab,
  (date: DateTime) => string
> = {
  [ScheduleViewTab.DAY]: (date: DateTime) => formatFullDateString(date),
  [ScheduleViewTab.WEEK]: (date: DateTime) => {
    const monthOfStartWeek = date.startOf('week').monthLong;
    const monthOfEndWeek = date.endOf('week').monthLong;
    const yearOfEndWeek = date.endOf('week').year;
    return `${monthOfStartWeek}${
      monthOfStartWeek !== monthOfEndWeek ? ` - ${monthOfEndWeek}` : ''
    } ${yearOfEndWeek}`;
  },
};

export const getScheduleViewDateHeader = (
  tab: ScheduleViewTab,
  date: DateTime,
): string => {
  return dateHeaderByScheduleViewMapper[tab](date);
};

/** Map recurrence frequency to the appropriate Recurrence enum (case-insensitive) */
export const getRecurrenceFrequency = (recurrence: string): Recurrence => {
  const compareTo = recurrence?.toLowerCase();
  if (compareTo === Recurrence.WEEKLY.toLowerCase()) {
    return Recurrence.WEEKLY;
  }
  if (compareTo === Recurrence.BIWEEKLY.toLowerCase()) {
    return Recurrence.BIWEEKLY;
  }
  return Recurrence.DOES_NOT_REPEAT;
};

/**
  The maximum date range for an availability query is 35 days between the earliest -> latest date.
  This translates to
    - 5 occurrences of a weekly event (35 days)
    - 3 occurrences for a bi-weekly event (35 days)
    - 2 occurrences for a monthly event (28 days)
*/
export const getQueryPeriodGroupSize = (frequency: Recurrence): number => {
  if (frequency === Recurrence.WEEKLY) return 5;
  if (frequency === Recurrence.BIWEEKLY) return 3;
  if (frequency === Recurrence.MONTHLY) return 2;
  return 1;
};

const recurrenceWeeksMapper: Record<Recurrence, number> = {
  [Recurrence.DOES_NOT_REPEAT]: 0,
  [Recurrence.WEEKLY]: 1,
  [Recurrence.BIWEEKLY]: 2,
  [Recurrence.MONTHLY]: 4,
};

/**
   Determine the query periods that should be checked for a recurring appointment up to
   six months from today. Organize the query periods into groups based on the maximum date
   range per availability query.
 */
export const getQueryPeriods = (
  end: string,
  recurrence: Recurrence,
  start: string,
  timezone: string,
  recurringUntilEndDate?: string,
): AppointmentDetailTime[][] => {
  const weeks = recurrenceWeeksMapper[recurrence];
  const queryPeriodGroupSize = getQueryPeriodGroupSize(recurrence);
  const recurrenceDuration = { days: CONFLICTS_RECURRENCE_CHECK_DAYS };
  const recurrenceEndDate = DateTime.now()
    .plus(recurrenceDuration)
    .endOf('day');
  const untilEndDateTime = DateTime.fromISO(recurringUntilEndDate)
    .setZone(timezone)
    .endOf('day');
  const maxLoopDate =
    recurringUntilEndDate && untilEndDateTime < recurrenceEndDate
      ? untilEndDateTime
      : recurrenceEndDate;
  const queryPeriodsGroup = [];
  let queryPeriods: AppointmentDetailTime[] = [];
  let loopDateEnd = DateTime.fromISO(end).setZone(timezone).plus({ weeks });
  let loopDateStart = DateTime.fromISO(start).setZone(timezone).plus({ weeks });
  while (loopDateEnd < maxLoopDate) {
    queryPeriods.push({
      end: toISORemoveMillis(loopDateEnd),
      start: toISORemoveMillis(loopDateStart),
    });
    if (queryPeriods.length === queryPeriodGroupSize) {
      queryPeriodsGroup.push(queryPeriods);
      queryPeriods = [];
    }
    loopDateEnd = loopDateEnd.plus({ weeks });
    loopDateStart = loopDateStart.plus({ weeks });
  }
  if (queryPeriods.length > 0) queryPeriodsGroup.push(queryPeriods);
  return queryPeriodsGroup;
};

export const getDurationInMinutes = (start: string, end: string): number =>
  Interval.fromDateTimes(DateTime.fromISO(start), DateTime.fromISO(end)).length(
    'minutes',
  );

export const getEndDateFromDuration = (
  start: string,
  minutes: number,
): string =>
  start
    ? DateTime.fromISO(start).plus({ minutes }).toJSDate().toISOString()
    : null;

export const checkOverlapAppointment = (
  appointment: AppointmentDetailResponse,
  targetAppointment: AppointmentDetailResponse,
): boolean => {
  const { appointmentStartDateTime: start, appointmentEndDateTime: end } =
    appointment;
  const {
    appointmentStartDateTime: targetStart,
    appointmentEndDateTime: targetEnd,
  } = targetAppointment;
  return start < targetEnd && end > targetStart;
};

export const overlapAppointmentsMapper = (
  appointments: AppointmentDetailResponse[],
): AppointmentDetailWeekView[] => {
  let overlapIdxes: number[] = [];
  const appointmentWidth: Record<number, number> = {};
  const appointmentOverlapIdx: Record<number, number> = {};
  appointments.forEach((appt: AppointmentDetailResponse, idx: number) => {
    if (
      idx !== appointments.length - 1 &&
      checkOverlapAppointment(appt, appointments[idx + 1])
    ) {
      overlapIdxes.push(idx);
    } else {
      overlapIdxes.forEach((idxEl: number, index: number) => {
        appointmentWidth[idxEl] = 100 / (overlapIdxes.length + 1);
        appointmentOverlapIdx[idxEl] = index;
      });
      appointmentWidth[idx] = 100 / (overlapIdxes.length + 1);
      appointmentOverlapIdx[idx] = overlapIdxes.length;
      overlapIdxes = [];
    }
  });
  return appointments.map((appt: AppointmentDetailResponse, idx: number) => ({
    ...appt,
    overlapIdx: appointmentOverlapIdx[idx],
    width: appointmentWidth[idx],
  }));
};

export const toAttendee = (
  participant: Participant,
  dateOfBirth?: string,
  relationToPatient?: string,
): AppointmentAttendee => {
  if (!participant?.isChecked) return null;

  const attendee = {
    externalId: participant.id,
    name: participant.primaryText,
    avatar: participant.avatar ?? null,
    outOfOffice: participant.outOfOfficeItems,
  } as AppointmentAttendee;

  if (participant.sub) attendee.providerType = participant.secondaryText;

  if (dateOfBirth) attendee.dateOfBirth = dateOfBirth;

  if (relationToPatient) attendee.relationToPatient = relationToPatient;

  return attendee;
};

// EventCard renders "()" for any defined pronouns array, so it receives only non-empty values.
export const getAttendeePronouns = (
  pronouns?: string[] | null,
): string[] | undefined => {
  const values = pronouns?.filter(Boolean);
  return values?.length > 0 ? values : undefined;
};

export const shouldShowPatientProfileLink = (
  patientExternalId: string,
): boolean =>
  !window.location.href.includes(
    `${window.location.origin}${Path.MAUD_PATIENTS}/${patientExternalId}`,
  );

export const roundDateToNearestInterval = (
  date: Date,
  intervalInMinutes = 15,
): Date => {
  const intervalInMillis = intervalInMinutes * 60 * 1000;
  return new Date(
    Math.ceil(date.getTime() / intervalInMillis) * intervalInMillis,
  );
};

export enum GroupClassCardTagLabel {
  HOSTING = 'Hosting',
  REGISTERED = 'Registered',
  UNREGISTERED = 'Register',
}

export const formatDateWithTimezone = (
  date: string,
  toFormat: string,
  timezone: string,
  toLowercase?: boolean,
): string => {
  const dateWithTimezone = DateTime.fromJSDate(new Date(date))
    .setZone(timezone)
    .toFormat(toFormat);
  return toLowercase ? dateWithTimezone.toLowerCase() : dateWithTimezone;
};

export const addTimezoneCodeToParticipantSecondaryText = (
  secondaryText: string,
  tzCode: string,
): string => {
  return `${secondaryText}${tzCode ? `, ${tzCode}` : ''}`;
};

export const removeTimezoneCodeFromParticipantSecondaryText = (
  secondaryText: string,
): string => {
  return secondaryText?.split(',')[0] ?? '';
};

/** The host's timezone can be derived by extracting the code from the "secondaryText" and mapping it to its corresponding value */
export const extractHostTimezone = (
  host: Participant,
  timezones: Enumeration[],
): string => {
  return (
    getTimezoneCodeToValueMapper(timezones)?.[
      host?.secondaryText?.indexOf(',') !== -1
        ? host?.secondaryText?.slice(host.secondaryText.indexOf(',') + 2)
        : DEFAULT_AVAILABILITY_SETTINGS_TIMEZONE_CODE
    ] ??
    getTimezoneCodeToValueMapper(timezones)[
      DEFAULT_AVAILABILITY_SETTINGS_TIMEZONE_CODE
    ]
  );
};
